/**
 * A small upload queue with per-file progress and per-file errors.
 *
 * Why a queue rather than one multi-file POST: the endpoint accepts a repeated
 * `files` key, but a single request gives one progress number for the whole
 * batch and buries per-file validation failures in an `errors[]` array that is
 * only readable after everything else has finished. Uploading one request per
 * file gives a real bar per row, lets a single bad file fail on its own, and
 * lets the user retry just that one.
 *
 * Concurrency is capped because uploads are processed synchronously server-side
 * (optimise + render every responsive variant, no Celery in this deployment),
 * and because the API is rate-limited — firing 30 parallel POSTs is a reliable
 * way to earn a 429 mid-batch.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getErrorMessage } from '@/services/api';
import { assetKeys } from '@/hooks/useAssets';
import {
  assetsService,
  validateUploadFile,
  type AssetConfig,
  type MediaAsset,
} from '@/services/assets';

export type UploadStatus = 'queued' | 'uploading' | 'done' | 'error' | 'cancelled';

export interface UploadItem {
  /** Local id — the file has no server id until it succeeds. */
  uid: string;
  file: File;
  name: string;
  size: number;
  /** `URL.createObjectURL` preview; revoked when the row leaves the queue. */
  previewUrl: string;
  status: UploadStatus;
  /** 0-100. Stops at 99 until the server responds (see `assetsService.upload`). */
  progress: number;
  error?: string;
  asset?: MediaAsset;
}

export interface UploadQueueOptions {
  /** Folder every file in this batch lands in. */
  folder?: string | null;
  /** Server limits; when present, files are pre-validated before any request. */
  config?: AssetConfig | null;
  /** Fired once per successful file, as it lands. */
  onUploaded?: (asset: MediaAsset) => void;
  /** Fired when the queue drains with at least one success. */
  onBatchComplete?: (assets: MediaAsset[]) => void;
  concurrency?: number;
}

let uidCounter = 0;
function nextUid(): string {
  uidCounter += 1;
  return `upload-${Date.now().toString(36)}-${uidCounter}`;
}

/** Images only — a folder drop hands us directories and stray text files too. */
function looksLikeImage(file: File): boolean {
  return file.type ? file.type.startsWith('image/') : /\.[a-z0-9]+$/i.test(file.name);
}

export function useAssetUploadQueue(options: UploadQueueOptions = {}) {
  const { folder = null, config = null, onUploaded, onBatchComplete, concurrency = 3 } = options;

  const [items, setItems] = useState<UploadItem[]>([]);
  const queryClient = useQueryClient();

  // Refs the driver effect reads, so changing a callback prop never restarts
  // an in-flight upload.
  const inFlight = useRef(new Set<string>());
  const controllers = useRef(new Map<string, AbortController>());
  const optionsRef = useRef({ folder, config, onUploaded, onBatchComplete });
  optionsRef.current = { folder, config, onUploaded, onBatchComplete };
  const batchResults = useRef<MediaAsset[]>([]);

  const patch = useCallback((uid: string, changes: Partial<UploadItem>) => {
    setItems((prev) => prev.map((item) => (item.uid === uid ? { ...item, ...changes } : item)));
  }, []);

  /** Add files. Rejected files still appear in the list, marked with why. */
  const enqueue = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files).filter(looksLikeImage);
      if (!incoming.length) return;
      const rows: UploadItem[] = incoming.map((file) => {
        const rejection = validateUploadFile(file, optionsRef.current.config);
        return {
          uid: nextUid(),
          file,
          name: file.name,
          size: file.size,
          previewUrl: URL.createObjectURL(file),
          status: rejection ? 'error' : 'queued',
          progress: 0,
          error: rejection?.message,
        };
      });
      setItems((prev) => [...prev, ...rows]);
    },
    [],
  );

  const retry = useCallback((uid: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.uid === uid && (item.status === 'error' || item.status === 'cancelled')
          ? { ...item, status: 'queued', progress: 0, error: undefined }
          : item,
      ),
    );
  }, []);

  const cancel = useCallback((uid: string) => {
    controllers.current.get(uid)?.abort();
    controllers.current.delete(uid);
    inFlight.current.delete(uid);
    setItems((prev) =>
      prev.map((item) =>
        item.uid === uid && (item.status === 'uploading' || item.status === 'queued')
          ? { ...item, status: 'cancelled', error: 'Cancelled' }
          : item,
      ),
    );
  }, []);

  const remove = useCallback((uid: string) => {
    controllers.current.get(uid)?.abort();
    controllers.current.delete(uid);
    inFlight.current.delete(uid);
    setItems((prev) => {
      const target = prev.find((item) => item.uid === uid);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.uid !== uid);
    });
  }, []);

  const clear = useCallback(() => {
    controllers.current.forEach((controller) => controller.abort());
    controllers.current.clear();
    inFlight.current.clear();
    setItems((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
    batchResults.current = [];
  }, []);

  /** Drop the rows that finished, keep the ones that still need attention. */
  const clearFinished = useCallback(() => {
    setItems((prev) => {
      const keep = prev.filter((item) => item.status !== 'done');
      prev.filter((item) => item.status === 'done').forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return keep;
    });
  }, []);

  // --- the driver ----------------------------------------------------------
  // Runs on every `items` change: starts as many queued uploads as the
  // concurrency budget allows. `inFlight` is a ref, not state, so React 18's
  // double-invoked effects cannot start the same file twice.
  useEffect(() => {
    const queued = items.filter((item) => item.status === 'queued' && !inFlight.current.has(item.uid));
    const free = Math.max(0, concurrency - inFlight.current.size);
    if (!queued.length || !free) return;

    queued.slice(0, free).forEach((item) => {
      inFlight.current.add(item.uid);
      const controller = new AbortController();
      controllers.current.set(item.uid, controller);
      patch(item.uid, { status: 'uploading', progress: 0, error: undefined });

      assetsService
        .upload(
          {
            file: item.file,
            folder: optionsRef.current.folder,
            // The filename is the only thing we know at upload time. Alt text
            // and the rest are edited afterwards in the library — pushing a
            // made-up alt text here would be worse than an empty one, because
            // it looks filled in to whoever audits accessibility later.
            title: '',
          },
          (percent) => patch(item.uid, { progress: percent }),
          controller.signal,
        )
        .then((asset) => {
          patch(item.uid, { status: 'done', progress: 100, asset });
          batchResults.current.push(asset);
          optionsRef.current.onUploaded?.(asset);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            patch(item.uid, { status: 'cancelled', error: 'Cancelled' });
            return;
          }
          patch(item.uid, { status: 'error', error: getErrorMessage(error) });
        })
        .finally(() => {
          inFlight.current.delete(item.uid);
          controllers.current.delete(item.uid);
        });
    });
  }, [items, concurrency, patch]);

  // Fire `onBatchComplete` once the queue drains, and refresh the library then
  // — not per file: a 30-image drop would otherwise fire 30 refetches of the
  // grid, the stats and the folder counts while the uploads are still running.
  const activeCount = items.filter((item) => item.status === 'queued' || item.status === 'uploading').length;
  const drained = useRef(true);
  useEffect(() => {
    if (activeCount > 0) {
      drained.current = false;
      return;
    }
    if (drained.current) return;
    drained.current = true;
    if (batchResults.current.length) {
      queryClient.invalidateQueries({ queryKey: assetKeys.all });
      optionsRef.current.onBatchComplete?.(batchResults.current);
      batchResults.current = [];
    }
  }, [activeCount, queryClient]);

  // Never leak object URLs when the dropzone unmounts mid-batch.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(
    () => () => {
      controllers.current.forEach((controller) => controller.abort());
      itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    },
    [],
  );

  return {
    items,
    enqueue,
    retry,
    cancel,
    remove,
    clear,
    clearFinished,
    isUploading: activeCount > 0,
    summary: {
      total: items.length,
      done: items.filter((item) => item.status === 'done').length,
      failed: items.filter((item) => item.status === 'error').length,
      active: activeCount,
    },
  };
}
